export const ABOUT_DESIGNATION_OPTIONS = [
  "Bishop of Udupi",
  "Youth Director",
  "Coordinator",
  "President, ICYM Udupi Diocese",
  "President, YCS Udupi Diocese",
  "Others",
] as const;

export type AboutDesignation = (typeof ABOUT_DESIGNATION_OPTIONS)[number];

export type AboutLeader = {
  docId?: string;
  name: string;
  designation: string;
  designationType: AboutDesignation;
  designationDetail?: string;
  place?: string;
  img?: string;
  rank: number;
};

export const defaultAboutLeaders: AboutLeader[] = [
  {
    name: "Most Rev. Dr. Gerald Isaac Lobo",
    designation: "Bishop of Udupi, Chairman Youth Commission",
    designationType: "Bishop of Udupi",
    img: "https://www.udupidiocese.in/images/stories/bishop.jpg",
    rank: 1,
  },
  {
    name: "Rev. Fr Steven Fernandes",
    designation: "Youth Director",
    designationType: "Youth Director",
    designationDetail: "",
    img: "/images/fr.steevan.jpg",
    rank: 2,
  },
  {
    name: "Mrs Priya Furtado",
    designation: "Coordinator",
    designationType: "Coordinator",
    place: "Mount Rosary Santhekatte",
    rank: 3,
  },
  {
    name: "Br Derick Mascarenhas",
    designation: "Coordinator",
    designationType: "Coordinator",
    place: "Kolalgiri",
    rank: 4,
  },
  {
    name: "Mr Alson Mathias",
    designation: "President, ICYM Udupi Diocese",
    designationType: "President, ICYM Udupi Diocese",
    place: "Kanajar",
    img: "/images/dexco/alson.png",
    rank: 5,
  },
  {
    name: "Mr Riyan Tellis",
    designation: "President, YCS Udupi Diocese",
    designationType: "President, YCS Udupi Diocese",
    place: "Attur",
    img: "/images/ycsDexco/riyan.jpeg",
    rank: 6,
  },
];

const defaultRankLookup = new Map(
  defaultAboutLeaders.map((member) => [`${member.name}::${member.designation}`, member.rank]),
);

export function getDefaultAboutRank(designationType: AboutDesignation) {
  switch (designationType) {
    case "Bishop of Udupi":
      return 1;
    case "Youth Director":
      return 2;
    case "Coordinator":
      return 3;
    case "President, ICYM Udupi Diocese":
      return 5;
    case "President, YCS Udupi Diocese":
      return 6;
    case "Others":
    default:
      return 7;
  }
}

export function buildAboutDesignation(
  designationType: AboutDesignation,
  designationDetail?: string,
) {
  if (designationType === "Youth Director") {
    const detail = designationDetail?.trim();
    return detail ? `Youth Director - ${detail}` : "Youth Director";
  }

  if (designationType === "Others") {
    return designationDetail?.trim() || "Others";
  }

  if (designationType === "Bishop of Udupi") {
    return "Bishop of Udupi, Chairman Youth Commission";
  }

  return designationType;
}

export function normalizeAboutLeader(
  leader: Omit<AboutLeader, "rank" | "designation"> &
    Partial<Pick<AboutLeader, "rank" | "designation">>,
): AboutLeader {
  const normalizedRank =
    leader.rank && leader.rank > 0
      ? leader.rank
      : defaultRankLookup.get(
          `${leader.name || ""}::${buildAboutDesignation(leader.designationType, leader.designationDetail)}`,
        ) || getDefaultAboutRank(leader.designationType);
  const normalizedDesignationType =
    normalizedRank === 5
      ? "President, ICYM Udupi Diocese"
      : normalizedRank === 6
        ? "President, YCS Udupi Diocese"
        : leader.designationType;
  const designation = buildAboutDesignation(
    normalizedDesignationType,
    normalizedDesignationType === "Youth Director" || normalizedDesignationType === "Others"
      ? leader.designationDetail
      : "",
  );
  const matchedDefaultRank = defaultRankLookup.get(`${leader.name || ""}::${designation}`);

  return {
    ...leader,
    designationType: normalizedDesignationType,
    designation,
    rank:
      leader.rank && leader.rank > 0
        ? normalizedRank
        : matchedDefaultRank || getDefaultAboutRank(leader.designationType),
  };
}

export function sortAboutLeaders(leaders: AboutLeader[]) {
  return [...leaders].sort((leaderA, leaderB) => {
    if (leaderA.rank !== leaderB.rank) {
      return leaderA.rank - leaderB.rank;
    }

    return leaderA.name.localeCompare(leaderB.name);
  });
}

export function buildAboutSummary(leaders: AboutLeader[]) {
  const bishop = leaders.find((leader) => leader.designationType === "Bishop of Udupi");
  const youthDirector = leaders.find((leader) => leader.designationType === "Youth Director");
  const coordinators = leaders.filter((leader) => leader.designationType === "Coordinator");
  const icymPresident =
    leaders.find((leader) => leader.rank === 5) ||
    leaders.find((leader) => leader.designationType === "President, ICYM Udupi Diocese");
  const ycsPresident =
    leaders.find((leader) => leader.rank === 6) ||
    leaders.find((leader) => leader.designationType === "President, YCS Udupi Diocese");

  const coordinatorText = coordinators.length
    ? coordinators.map((leader) => leader.name).join(" and ")
    : "the coordinators";
  const youthDirectorName =
    youthDirector?.name || "the Youth Director of the Diocese of Udupi";
  const icymPresidentText = icymPresident?.name
    ? `${icymPresident.name}, President of ICYM Udupi Diocese`
    : "the President of ICYM Udupi Diocese";
  const ycsPresidentText = ycsPresident?.name
    ? `${ycsPresident.name}, President of YCS Udupi Diocese`
    : "the President of YCS Udupi Diocese";

  return `The Youth Commission of the Diocese of Udupi functions under the leadership of ${
    bishop?.name || "the Bishop of Udupi"
  } and ${
    youthDirectorName
  }, Youth Director of the Diocese of Udupi. ${youthDirectorName} also serves as the Secretary of the Youth Commission and oversees the various youth movements including ICYM, YCS, and YSM. The Youth Commission team includes ${coordinatorText} as Coordinators, ${
    icymPresidentText
  }, and ${
    ycsPresidentText
  }, along with the deanery directors. The Youth Commission office is located at ANUGRAHA Pastoral Centre, Kakkunje, Santhekatte. The Diocese of Udupi is divided into five deaneries.`;
}
